import type {
  Operator, Predicate, PredicatedProcessor, ProcessorFn,
} from './types';


export function createPredicatedProcessor<T>(args: {
  operator?: Operator;
  predicates: Predicate<T>[];
  process: ProcessorFn<T>;
}): PredicatedProcessor<T> {
  const {
    operator = 'every',
    predicates,
    process,
  } = args;

  return {
    operator,
    predicates,
    process,
  };
}


/**
 * Function that executes a registry of PredicatedProcessors against a list of items
 */
export function executePredicatedProcessors<T>(args: {
  items: T[];
  processorRegistry: Record<string, PredicatedProcessor<T>>;
}) {
  const { items, processorRegistry } = args;

  for (const processor of Object.values(processorRegistry)) {
    const { operator, predicates, process } = processor;

    /**
     * Builds an array of arrays of type T, where each sub-array contains all items
     * that match a predicate
     */
    const matchingItemsPerPredicate = predicates.map(p => items.filter(i => p(i)));
    // Represents the number of predicates that evaluated to true at least once
    const countOfTruthyPredicates = matchingItemsPerPredicate
      .filter(matchingItems => matchingItems.length > 0).length;
    /**
     * Run the process function on an 'every' when *all* predicates evaluated
     * to truthy at least once
     */
    if (operator === 'every' && countOfTruthyPredicates === predicates.length) {
      process(...matchingItemsPerPredicate);
    }

    /**
     * Run the process function on an 'some' when *any* predicate evaluated
     * to truthy at least once
     */
    if (operator === 'some' && countOfTruthyPredicates > 0) {
      process(...matchingItemsPerPredicate);
    }
  }
}
